import { useState } from "react"
import { Calendar as CalendarIcon } from "lucide-react"
import { format } from "date-fns"
import { Button } from "@/modules/shared/ui/components/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/modules/shared/ui/components/popover"
import { ZoomableCalendar } from "@/modules/shared/ui/components/zoomable-calendar"
import { cn } from "@/lib/utils"

export interface DatePickerProps {
  value?: Date
  onChange: (date: Date | undefined) => void
  placeholder?: string
  disabled?: React.ComponentProps<typeof ZoomableCalendar>["disabled"]
  align?: "start" | "center" | "end"
  buttonClassName?: string
  popoverClassName?: string
  calendarClassName?: string
  startYear?: number
  endYear?: number
  defaultMonth?: Date
  isDisabled?: boolean
  id?: string
  onBlur?: () => void
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled,
  align = "start",
  buttonClassName,
  popoverClassName,
  calendarClassName,
  startYear,
  endYear,
  defaultMonth,
  isDisabled = false,
  id,
  onBlur,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) onBlur?.()
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={isDisabled}
          className={cn(
            "w-full justify-start text-left font-normal bg-card border-border hover:bg-card hover:border-primary/50 focus:ring-2 focus:ring-primary/20",
            !value && "text-muted-foreground",
            buttonClassName,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-auto p-0 bg-card", popoverClassName)}
        align={align}
      >
        <ZoomableCalendar
          mode="single"
          open={open}
          selected={value}
          defaultMonth={defaultMonth}
          startYear={startYear}
          endYear={endYear}
          onSelect={(date) => {
            if (date) date.setHours(0, 0, 0, 0)
            onChange(date)
            setOpen(false)
          }}
          disabled={disabled}
          className={cn(
            "rounded-md border border-border shadow-md bg-card pointer-events-auto",
            calendarClassName,
          )}
        />
      </PopoverContent>
    </Popover>
  )
}
